import React, {
    useEffect, useMemo, useState, useCallback,
} from 'react';
import { faDownload, faRocket } from '@fortawesome/free-solid-svg-icons';
import {
    NSButton, NSCard, NSSelect, NSSwitch,
} from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import {
    SELECTED_SCENARIO_KEY, PRO_FORMA_TABS, PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT, LOADING_TEXT,
} from 'views/ProFormaTable/constants';
import { handleExportProFormaToExcel } from 'views/ProFormaTable/helpers/excelExport';
import useToast from 'hooks/useToast';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { IOptionAnyValue } from 'bricks/types';
import { useGetProjectBudgetScenarios } from 'queries/BudgetScenario/useGetProjectBudgetScenarios';
import { ISignUpManager } from 'views/public/SignUp/useSignUpManager';
import { useLocalStorage, GlobalKeys } from 'helpers/localStorage';
import { getDefaultFooterKeyStats, getKeyStatsActualsDiscrepancies, getPartnerReturnFooterKeyStats } from 'views/ProFormaTable/helpers';
import { IKeyStatsCard, ISideNavFooterKeyStatDiscrepancy } from 'views/ProFormaTable/types';
import { faMemoCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { URLS } from 'services/urls';
import { ProFormaModelIds } from 'constants/proForma';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';
import CreateProjectModal from '../CreateProjectModal/CreateProjectModal';
import SignUpSection from '../SignUpSection/SignUpSection';
import ProFormaSideNavSettingsMenu from './ProFormaSideNavSettingsMenu/ProFormaSideNavSettingsMenu';
import { ProFormaFooterKeyStatCard } from './ProFormaFooterKeyStatCard/ProFormaFooterKeyStatCard';

export interface IProFormaSideNavFooterProps {
    isCollapsed?: boolean;
    signUpMode?: boolean;
    currentTab?: string;
    setCurrentTab?: (tabName: string) => void;
    setDisplayManualSignUp?: (display: boolean) => void;
    signUpManager?: ISignUpManager;
}

const ProFormaSideNavFooter = ({
    isCollapsed, signUpMode = false,
    currentTab, setCurrentTab, setDisplayManualSignUp, signUpManager,
}: IProFormaSideNavFooterProps) => {
    const {
        proForma, shouldUseProjectActuals, setShouldUseProjectActuals, returnsData,
        showCents, updateLocalStorageByKey, getLocalStorageValueByKey, waterfallAnalysis,
        projectActuals, disposition, uses, sources, operatingPeriodCashFlows, operatingInfo,
        incomeSummary,
    } = useProFormaSocketContext();
    const { showSuccess, showError } = useToast();
    const [isExporting, setIsExporting] = useState(false);
    const [showPartnerReturns, setShowPartnerReturns] = useLocalStorage<boolean>(GlobalKeys.ShowPartnerReturns, false);
    const [proFormaExistingKeyStats, setProFormaExistingKeyStats] = useState<IKeyStatsCard[][]>([]);
    const [proFormaExistingPartnerReturnKeyStats, setProFormaExistingPartnerReturnKeyStats] = useState<IKeyStatsCard[][]>([]);

    const { data: budgetScenariosData, isLoading: isLoadingScenarios } = useGetProjectBudgetScenarios(
        proForma?.subprojectId?.toString() || '',
    );

    const scenarioOptions = budgetScenariosData?.budgetScenarios?.map(scenario => ({
        label: scenario.name,
        value: scenario.id,
    })) || [];

    const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

    const localStorageScenarioId = getLocalStorageValueByKey(SELECTED_SCENARIO_KEY)
        ? Number(getLocalStorageValueByKey(SELECTED_SCENARIO_KEY)) : null;
    const initiallySelectedOption = localStorageScenarioId && scenarioOptions.find(option => option.value === localStorageScenarioId);
    const [selectedScenario, setSelectedScenario] = useState<{ label: string; value: number }>(
        initiallySelectedOption || (scenarioOptions[0] ?? { label: '', value: 0 }),
    );

    const showCentsFormat = showCents ? '$0,0.00' : '$0,0';

    const returns = shouldUseProjectActuals && projectActuals ? projectActuals.returns : returnsData;
    const waterfallAnalysisData = shouldUseProjectActuals && projectActuals ? projectActuals.waterfallAnalysis : waterfallAnalysis;
    const projectOverviewUrl = budgetScenariosData?.subprojectId
        ? URLS.PROJECTS_APP.PROJECT_DASHBOARD_V2(budgetScenariosData.subprojectId)
        : undefined;

    const addressFieldsSet = !!proForma.address?.trim() && !!proForma.city?.trim() && !!proForma.state?.trim() && !!proForma.zipCode?.trim();

    // Check if required values are provided based on pro forma model type
    const isKeyStatsInProgress = useMemo(() => {
        if (!proForma?.proFormaModelId) return false;

        const hasRequiredGrossSF = !!proForma.builtGrossSquareFootage;
        let hasRequiredValues = false;

        switch (proForma.proFormaModelId) {
            case ProFormaModelIds.APARTMENT:
            case ProFormaModelIds.APARTMENT_WITH_RETAIL:
            case ProFormaModelIds.STUDENT_HOUSING:
            case ProFormaModelIds.LAND_DEVELOPMENT:
            case ProFormaModelIds.HOTEL_HOSPITALITY:
            case ProFormaModelIds.RESIDENTIAL_CONDO:
            case ProFormaModelIds.SELF_STORAGE:
                hasRequiredValues = hasRequiredGrossSF && !!proForma.estimatedUnits;
                break;
            case ProFormaModelIds.INDUSTRIAL:
            case ProFormaModelIds.RETAIL:
            case ProFormaModelIds.OFFICE:
                hasRequiredValues = hasRequiredGrossSF;
                break;
            case ProFormaModelIds.DATA_CENTER:
                hasRequiredValues = hasRequiredGrossSF && !!proForma.estimatedRacks;
                break;
            default:
                throw new Error('Unexpected Pro Forma model.');
        }

        return !hasRequiredValues;
    }, [proForma]);

    const getStatsInProgressIfRequired = useCallback((stats: IKeyStatsCard[][]) => {
        if (isKeyStatsInProgress) {
            return stats.map(row => row.map(stat => ({
                ...stat,
                value: null, // Enforces "In Progress" state
            })));
        }
        return stats;
    }, [isKeyStatsInProgress]);

    const proFormaKeyStats = useMemo(() => {
        const stats = getDefaultFooterKeyStats(returns, showCentsFormat);
        return getStatsInProgressIfRequired(stats);
    }, [
        returns,
        showCentsFormat,
        getStatsInProgressIfRequired,
    ]);

    const proFormaPartnerReturnKeyStats = useMemo(() => {
        const stats = getPartnerReturnFooterKeyStats(waterfallAnalysisData, showCentsFormat);
        return getStatsInProgressIfRequired(stats);
    }, [
        waterfallAnalysisData,
        showCentsFormat,
        getStatsInProgressIfRequired,
    ]);

    let keyStatsActualsDiscrepancies: Record<string, ISideNavFooterKeyStatDiscrepancy> = {};

    if (shouldUseProjectActuals && projectActuals) {
        keyStatsActualsDiscrepancies = getKeyStatsActualsDiscrepancies(
            proFormaExistingKeyStats.flat(),
            proFormaExistingPartnerReturnKeyStats.flat(),
            proFormaKeyStats.flat(),
            proFormaPartnerReturnKeyStats.flat(),
        );
    }

    const renderKeyStatRow = (statRow: any[], rowIndex: number | string) => (
        <div key={`stat-row-${rowIndex}`} className="d-flex">
            {statRow.map((stat, index) => ProFormaFooterKeyStatCard({
                index,
                stat,
                keyStatsActualsDiscrepancies,
                shouldAnimate: proForma.proFormaStatusId !== PRO_FORMA_STATUS.PROJECT_CREATED,
            }))}
        </div>
    );

    const handleSelectScenario = (selectedScenario: IOptionAnyValue) => {
        setSelectedScenario(selectedScenario);
        updateLocalStorageByKey(SELECTED_SCENARIO_KEY, selectedScenario.value);
    };

    const handleToggleUseProjectActuals = () => {
        const newValue = !shouldUseProjectActuals;
        setShouldUseProjectActuals(newValue);

        if (newValue && scenarioOptions.length > 0) {
            const storageScenarioStillExists = scenarioOptions.find(option => option.value === localStorageScenarioId);
            if (!storageScenarioStillExists) {
                const firstScenario = scenarioOptions[0];
                setSelectedScenario(firstScenario);
                updateLocalStorageByKey(SELECTED_SCENARIO_KEY, firstScenario.value);
            }
        }
    };

    const handleToggleShowPartnerReturns = () => setShowPartnerReturns(!showPartnerReturns);

    const handleExportToExcel = async () => {
        setIsExporting(true);
        try {
            const saleMonth = operatingInfo?.saleMonth;
            const clampedCashFlows = saleMonth !== undefined
                ? operatingPeriodCashFlows.slice(0, saleMonth + 1)
                : operatingPeriodCashFlows;
            await handleExportProFormaToExcel({
                proForma,
                operatingInfo,
                incomeSummary,
                usesSummary: uses,
                sourcesSummary: sources,
                cashFlows: clampedCashFlows,
                disposition,
                returns: returnsData,
                showCents,
                includeGainLossToLease: operatingInfo?.includeGainLossToLease ?? true,
            });
            showSuccess('Pro forma exported successfully');
        } catch {
            showError('Failed to export pro forma. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // Cache pro forma stats when NOT in project actuals mode and data is loaded
    // This cache is used for discrepancy calculation when viewing project actuals
    useEffect(() => {
        const hasData = proFormaKeyStats.flat().some(stat => stat.value !== null && stat.value !== undefined);
        if (!shouldUseProjectActuals && hasData) {
            setProFormaExistingKeyStats(proFormaKeyStats);
            setProFormaExistingPartnerReturnKeyStats(proFormaPartnerReturnKeyStats);
        }
    }, [shouldUseProjectActuals, proFormaKeyStats, proFormaPartnerReturnKeyStats]);

    useEffect(() => {
        if (initiallySelectedOption && localStorageScenarioId !== selectedScenario.value) setSelectedScenario(initiallySelectedOption);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initiallySelectedOption]);

    const enabledForExport = [
        ProFormaModelIds.APARTMENT,
        ProFormaModelIds.APARTMENT_WITH_RETAIL,
        ProFormaModelIds.INDUSTRIAL,
        ProFormaModelIds.OFFICE,
        ProFormaModelIds.RETAIL,
        ProFormaModelIds.DATA_CENTER,
        ProFormaModelIds.STUDENT_HOUSING
    ].includes(proForma.proFormaModelId);

    return (
        <>
            <NSCard className="NSCard--level-4 p-2 pt-1 mt-2 ProFormaSideNav__footer w-100">
                {signUpMode && !isCollapsed && setCurrentTab && setDisplayManualSignUp && signUpManager
                    && currentTab === PRO_FORMA_TABS.GENERAL_INFORMATION.name && (
                    <>
                        <SignUpSection
                            setCurrentTab={setCurrentTab}
                            setDisplayManualSignUp={setDisplayManualSignUp}
                            signUpManager={signUpManager}
                        />
                        <hr className="mt-1" />
                    </>
                )}
                <RenderIf isTrue={proForma.proFormaStatusId === PRO_FORMA_STATUS.PROJECT_CREATED}>
                    <div className="ProFormaSideNav__footer__display-none d-flex justify-content-between mb-2">
                        <div className="d-flex">
                            <IconWithTooltip
                                name="project-actuals-icon"
                                icon={faMemoCircleCheck}
                                tooltipText={PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT}
                                className="my-auto mr-1"
                            />
                            <span className="text-dark">Use project actuals</span>
                        </div>

                        <NSSwitch
                            id="useProjectActuals"
                            name="useProjectActuals"
                            checked={shouldUseProjectActuals}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleToggleUseProjectActuals()}
                            containerClassName="text-white heap-deal-pro-forma-show-project-actuals-toggle"
                        />

                    </div>
                    <NSCard
                        className="ProFormaSideNav__footer__display-none
                         p-2 mb-2 w-100 text-muted position-relative overflow-hidden font-13 NSCard--level-5"
                    >
                        <div>{budgetScenariosData?.developmentSiteName || LOADING_TEXT}</div>
                        <div className="text-dark font-weight-bold">
                            {projectOverviewUrl ? (
                                <a
                                    href={projectOverviewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-dark"
                                >
                                    {budgetScenariosData?.subprojectName || LOADING_TEXT}
                                </a>
                            ) : (
                                budgetScenariosData?.subprojectName || LOADING_TEXT
                            )}
                        </div>
                        <RenderIf isTrue={shouldUseProjectActuals && budgetScenariosData?.isEarlyPlanning} isLoading={isLoadingScenarios}>
                            <NSSelect
                                name="projectScenarioSelect"
                                formClassName="pt-1"
                                options={scenarioOptions}
                                value={selectedScenario}
                                onChange={option => handleSelectScenario(option)}
                                isClearable={false}
                                isSearchable={false}
                                menuPosition="fixed"
                                isDisabled={isLoadingScenarios}
                                styles={{
                                    ...customStyles,
                                    control: styles => ({
                                        ...styles,
                                        border: 'none',
                                        backgroundColor: '#4F5A6A',
                                    }),
                                }}
                            />
                        </RenderIf>
                    </NSCard>
                </RenderIf>

                {proFormaKeyStats.map((statRow, rowIndex) => renderKeyStatRow(statRow, rowIndex))}

                <RenderIf isTrue={showPartnerReturns}>
                    {proFormaPartnerReturnKeyStats.map((statRow, rowIndex) => renderKeyStatRow(statRow, `partner-${rowIndex}`))}
                </RenderIf>

                <RenderIf isTrue={!signUpMode}>
                    <div className="ProFormaSideNav__footer__buttons-container mt-2 d-flex align-items-center p-0">
                        <ProFormaSideNavSettingsMenu
                            showPartnerReturns={showPartnerReturns}
                            handleToggleShowPartnerReturns={handleToggleShowPartnerReturns}
                        />
                        <NSButton
                            id="export-pro-forma-btn"
                            icon={faDownload}
                            className="mr-1 NSButton--secondary-lighten"
                            ariaLabel="Export pro forma to Excel"
                            callback={handleExportToExcel}
                            disabled={isExporting || !enabledForExport}
                            disabledTooltip={!enabledForExport ? 'Excel export coming soon!' : undefined}
                        />

                        <RenderIf isTrue={proForma.proFormaStatusId !== PRO_FORMA_STATUS.PROJECT_CREATED}>
                            <NSButton
                                id="create-project-from-deal-btn"
                                text={isCollapsed ? '' : 'Create project from deal'}
                                className={`w-100 heap-deal-pro-forma-create-project-from-deal ${!isCollapsed && 'text-truncate'}`}
                                icon={faRocket}
                                color="primary"
                                callback={() => setIsCreateProjectModalOpen(true)}
                                disabled={
                                    proForma.proFormaStatusId !== PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE
                                    || !addressFieldsSet
                                    || isKeyStatsInProgress
                                }
                                disabledTooltip="Pro forma must be substantially complete and address fields set before creating a project"
                            />
                        </RenderIf>
                    </div>
                </RenderIf>
            </NSCard>
            <CreateProjectModal
                toggle={() => setIsCreateProjectModalOpen(false)}
                isOpen={isCreateProjectModalOpen}
            />
        </>

    );
};

export default ProFormaSideNavFooter;
